import { InputField, TextAreaField } from 'lib/iterable-hook-form';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { FlexSpacer } from 'ui/flexSpacer';
import { FormField } from 'ui/formField';
import { Overline, colors, monospace2, spacing } from 'ui/theme';
import { EMAIL_FIELD_NAMES, PLACEHOLDER_URL } from '../../constants';
import { ProjectSettingsFormSchemaType } from '../../contexts';
import { ConnectedFormField, MarkdownDescription, OptionalLabelText } from './ConnectedFormField';
import { DescribedCheckbox } from './DescribedCheckbox';
import { DisclosureCheckbox } from './DisclosureCheckbox';
import { FormSection } from './FormSection';

const {
  ADD_UNSUBSCRIBE,
  CUSTOM_UNSUBSCRIBE_SNIPPET,
  FIRST_NAME_FIELD,
  HOSTED_UNSUBSCRIBE_URL,
  LAST_NAME_FIELD,
  ENABLE_SENDING_DOMAIN,
  SENDING_DOMAIN,
  SUBSCRIBE_SUCCESS_URL,
  USE_DOUBLE_OPT_OUT,
  USE_DYNAMIC_SENDERS,
  USE_HOSTED_UNSUBSCRIBE_URL_IN_LIST_UNSUBSCRIBE_HEADER,
} = EMAIL_FIELD_NAMES;

const SecondaryOverline = styled(Overline)`
  display: block;
  margin-top: ${spacing.plusHalf};
  margin-bottom: ${spacing.plusHalf};
  color: ${colors.textSecondary};
`;

const SelectRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${spacing.base};
`;

/**
 * Form field closely associated with the previous input field
 * Separated fields to give space for between for error messages
 * Added specificity to override & + & styles
 */
const StyledFormField = styled(FormField)`
  && {
    margin-top: ${spacing.half};
  }
`;

const StyledTextArea = styled(TextAreaField)`
  > textarea {
    ${monospace2};
  }
`;

export const EMAIL_SECTION_TEXT = {
  /** Email section */
  sectionHeadingText: 'Email',
  sectionId: 'email',
  /** To Name fields */
  firstNamePlaceholderText: 'firstName',
  lastNamePlaceholderText: 'lastName',
  toNameFieldDescriptionText:
    'Add a first and/or last name field to personalize email’s ‘to name’ field.',
  toNameFieldLabelText: '‘To Name’ field',
  /** use dynmaic senders */
  useDynamicSendersDescriptionText:
    'Enables dynamically-assigned email senders based on user fields. Additional setup required.',
  useDynamicSendersDescriptionTextId: 'useDynamicSendersDescriptionText',
  useDynamicSendersLabelText: 'Use dynamic sender emails',
  useDynamicSendersSupportUrl: 'https://support.iterable.com/hc/articles/115001359046',
  /** sending domain */
  sendingDomainDescriptionText:
    'Set a custom domain to send email from Iterable’s Amazon SES shared IPs.',
  sendingDomainDescriptionTextId: 'sendingDomainDescriptionText',
  sendingDomainLabelText: 'Custom sending domain for shared IPs',
  sendingDomainSupportURL: 'https://support.iterable.com/hc/articles/360016155252',
  sendingDomainWarningText:
    "This affects records on the DNS Setup page and disables senders that don't match this sending domain.",
  /** add unsubscribe */
  addUnsubscribeDescriptionText:
    'Automatically insert an unsubscribe block at the footer of marketing emails.',
  addUnsubscribeLabelText: 'Auto-append unsubscribe block',
  /** use double opt-out */
  useDoubleOptOutDescriptionText:
    'Require users to confirm email unsubscribe with an additional click. May negatively impact sender reputation as compared to single opt-out.',
  useDoubleOptOutLabelText: 'Double opt-out for unsubscribes',
  /** custom unsubscribe snippet */
  customUnsubscribeSnippetDescriptionText: 'Included on the Iterable-hosted unsubscribe page.',
  customUnsubscribeSnippetLabelText: 'Custom unsubscribe HTML snippet',
  /** hosted unsubscribe url */
  hostedUnsubscribeDescriptionText:
    'The URL for a page on your website that manages user subscription preferences.',
  hostedUnsubscribeDescriptionTextId: 'hostedUnsubscribeDescriptionText',
  hostedUnsubscribeLabelText: 'Hosted unsubscribe URL',
  hosetdUnsubscribeSupportUrl: 'https://support.iterable.com/hc/articles/208463956',
  /** hosted unsubscribe url used in list header */
  listHeaderLabelDescriptionText: 'Iterable-hosted unsubscribe page URL is used by default.',
  listHeaderLabelDescriptionTextId: 'listHeaderLabelDescriptionText',
  listHeaderLabelText: "Use as the 'List-Unsubscribe' mail header value",
  listHeaderLabelSupportUrl: 'https://support.iterable.com/hc/articles/24886914774548',
  /** subscribe success url */
  subscribeSuccessUrlDescriptionText:
    'Redirect users to a custom landing page after successfully subscribing to a list using an Iterable-generated signup form.',
  subscribeSuccessUrlLabelText: 'Subscribe success redirect URL',
};

export const EmailFormSection = () => {
  const { watch } = useFormContext<ProjectSettingsFormSchemaType>();
  const hostedUnsubscribeUrl = watch(HOSTED_UNSUBSCRIBE_URL);
  const useDoubleOptOut = watch(USE_DOUBLE_OPT_OUT);

  return (
    <FormSection
      id={EMAIL_SECTION_TEXT.sectionId}
      data-test={`${EMAIL_SECTION_TEXT.sectionId}-form-section`}
      headingText={EMAIL_SECTION_TEXT.sectionHeadingText}
    >
      <SecondaryOverline>Sender</SecondaryOverline>
      <FormField
        descriptionText={EMAIL_SECTION_TEXT.toNameFieldDescriptionText}
        labelText={<OptionalLabelText text={EMAIL_SECTION_TEXT.toNameFieldLabelText} />}
      >
        <SelectRow>
          <InputField
            name={FIRST_NAME_FIELD}
            placeholder={EMAIL_SECTION_TEXT.firstNamePlaceholderText}
          />
          <InputField
            name={LAST_NAME_FIELD}
            placeholder={EMAIL_SECTION_TEXT.lastNamePlaceholderText}
          />
        </SelectRow>
      </FormField>
      <ConnectedFormField name={USE_DYNAMIC_SENDERS}>
        <DescribedCheckbox
          label={EMAIL_SECTION_TEXT.useDynamicSendersLabelText}
          DescriptionText={
            <MarkdownDescription
              id={EMAIL_SECTION_TEXT.useDynamicSendersDescriptionTextId}
              markdownText={EMAIL_SECTION_TEXT.useDynamicSendersDescriptionText}
              supportUrl={EMAIL_SECTION_TEXT.useDynamicSendersSupportUrl}
            />
          }
          name={USE_DYNAMIC_SENDERS}
        />
      </ConnectedFormField>

      <ConnectedFormField name={ENABLE_SENDING_DOMAIN}>
        <FlexSpacer alignItems="stretch" flexDirection="column" gap="half">
          <DisclosureCheckbox
            DescriptionText={
              <MarkdownDescription
                id={EMAIL_SECTION_TEXT.sendingDomainDescriptionTextId}
                markdownText={EMAIL_SECTION_TEXT.sendingDomainDescriptionText}
                supportUrl={EMAIL_SECTION_TEXT.sendingDomainSupportURL}
              />
            }
            label={EMAIL_SECTION_TEXT.sendingDomainLabelText}
            name={ENABLE_SENDING_DOMAIN}
          >
            <ConnectedFormField
              name={SENDING_DOMAIN}
              helpText={EMAIL_SECTION_TEXT.sendingDomainWarningText}
            >
              <InputField name={SENDING_DOMAIN} placeholder={PLACEHOLDER_URL} />
            </ConnectedFormField>
          </DisclosureCheckbox>
        </FlexSpacer>
      </ConnectedFormField>

      <SecondaryOverline>Subscriptions</SecondaryOverline>

      <ConnectedFormField name={ADD_UNSUBSCRIBE}>
        <DescribedCheckbox
          DescriptionText={EMAIL_SECTION_TEXT.addUnsubscribeDescriptionText}
          label={EMAIL_SECTION_TEXT.addUnsubscribeLabelText}
          name={ADD_UNSUBSCRIBE}
        />
      </ConnectedFormField>

      <ConnectedFormField name={USE_DOUBLE_OPT_OUT}>
        <DescribedCheckbox
          DescriptionText={EMAIL_SECTION_TEXT.useDoubleOptOutDescriptionText}
          label={EMAIL_SECTION_TEXT.useDoubleOptOutLabelText}
          name={USE_DOUBLE_OPT_OUT}
        />
      </ConnectedFormField>

      {!!useDoubleOptOut && (
        <FormField
          descriptionText={EMAIL_SECTION_TEXT.customUnsubscribeSnippetDescriptionText}
          labelText={EMAIL_SECTION_TEXT.customUnsubscribeSnippetLabelText}
        >
          <StyledTextArea name={CUSTOM_UNSUBSCRIBE_SNIPPET} />
        </FormField>
      )}

      <ConnectedFormField
        descriptionText={
          <MarkdownDescription
            id={EMAIL_SECTION_TEXT.hostedUnsubscribeDescriptionTextId}
            markdownText={EMAIL_SECTION_TEXT.hostedUnsubscribeDescriptionText}
            supportUrl={EMAIL_SECTION_TEXT.hosetdUnsubscribeSupportUrl}
          />
        }
        labelText={<OptionalLabelText text={EMAIL_SECTION_TEXT.hostedUnsubscribeLabelText} />}
        name={HOSTED_UNSUBSCRIBE_URL}
      >
        <InputField name={HOSTED_UNSUBSCRIBE_URL} placeholder={PLACEHOLDER_URL} />
      </ConnectedFormField>

      <StyledFormField>
        <DescribedCheckbox
          DescriptionText={
            <MarkdownDescription
              id={EMAIL_SECTION_TEXT.listHeaderLabelDescriptionTextId}
              markdownText={EMAIL_SECTION_TEXT.listHeaderLabelDescriptionText}
              supportUrl={EMAIL_SECTION_TEXT.listHeaderLabelSupportUrl}
            />
          }
          isDisabled={!hostedUnsubscribeUrl}
          label={EMAIL_SECTION_TEXT.listHeaderLabelText}
          name={USE_HOSTED_UNSUBSCRIBE_URL_IN_LIST_UNSUBSCRIBE_HEADER}
        />
      </StyledFormField>

      <ConnectedFormField
        labelText={<OptionalLabelText text={EMAIL_SECTION_TEXT.subscribeSuccessUrlLabelText} />}
        descriptionText={EMAIL_SECTION_TEXT.subscribeSuccessUrlDescriptionText}
        name={SUBSCRIBE_SUCCESS_URL}
        inputId={SUBSCRIBE_SUCCESS_URL}
      >
        <InputField
          id={SUBSCRIBE_SUCCESS_URL}
          name={SUBSCRIBE_SUCCESS_URL}
          placeholder={PLACEHOLDER_URL}
        />
      </ConnectedFormField>
    </FormSection>
  );
};
